// Admin > Saúde de Gravações de Reuniões
// Mostra, por dia, o total de reuniões, % com gravação, e lista das que falharam.
// Permite identificar rapidamente quando a taxa cai abaixo do limite saudável.

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Activity, AlertTriangle, CheckCircle2, ExternalLink, Video, RefreshCcw, PhoneForwarded } from "lucide-react";
import { toast } from "sonner";

type Period = "7d" | "14d" | "30d";

interface MeetingRow {
  id: string;
  scheduled_for: string | null;
  started_at: string | null;
  ended_at: string | null;
  duration_minutes: number | null;
  status: string | null;
  recorded: boolean | null;
  recording_url: string | null;
  daily_recording_id: string | null;
  daily_room_name: string | null;
  contact_id: string | null;
  deal_id: string | null;
  created_by: string | null;
}

const PERIOD_DAYS: Record<Period, number> = { "7d": 7, "14d": 14, "30d": 30 };

function startOfDayISO(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.toISOString();
}

function dayKey(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export default function MeetingHealthDashboard() {
  const [period, setPeriod] = useState<Period>("7d");
  const [recovering, setRecovering] = useState<string | null>(null);

  const sinceISO = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - PERIOD_DAYS[period]);
    return startOfDayISO(d);
  }, [period]);

  const { data: meetings, isLoading, refetch } = useQuery({
    queryKey: ["admin-meeting-health", period],
    queryFn: async (): Promise<MeetingRow[]> => {
      const { data, error } = await supabase
        .from("meetings")
        .select(
          "id, scheduled_for, started_at, ended_at, duration_minutes, status, recorded, recording_url, daily_recording_id, daily_room_name, contact_id, deal_id, created_by",
        )
        .gte("scheduled_for", sinceISO)
        .order("scheduled_for", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data || []) as MeetingRow[];
    },
    staleTime: 60_000,
  });

  // Saúde de transferências de chamadas (cold/queue) — gravação contínua pós-transfer
  const { data: transfers } = useQuery({
    queryKey: ["admin-transfer-health", period],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("calls")
        .select("id, transferred_at, transferred_to, transfer_type, duration_seconds, deal_id, contact_id, metadata, status")
        .not("transferred_at", "is", null)
        .gte("transferred_at", sinceISO)
        .order("transferred_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return data || [];
    },
    staleTime: 60_000,
  });

  const transferStats = useMemo(() => {
    const list = transfers || [];
    const total = list.length;
    // "Com gravação pós-transfer" = metadata.transfer_completed_at presente OU duration > segmento inicial
    const withFullRec = list.filter((t: any) => {
      const meta = t.metadata && typeof t.metadata === "object" ? t.metadata : {};
      return !!meta.transfer_completed_at || (t.duration_seconds && t.duration_seconds > 30);
    });
    const failed = list.filter((t: any) => {
      const meta = t.metadata && typeof t.metadata === "object" ? t.metadata : {};
      return !meta.transfer_completed_at && (t.duration_seconds ?? 0) <= 30;
    });
    const rate = total > 0 ? (withFullRec.length / total) * 100 : 100;
    return { total, withFullRec: withFullRec.length, failed, rate };
  }, [transfers]);

  const stats = useMemo(() => {
    const all = meetings || [];
    // Only "real" meetings: those that started or have a duration
    const happened = all.filter(
      (m) => m.started_at || (m.duration_minutes && m.duration_minutes > 0) || m.status === "completed",
    );
    const withRec = happened.filter((m) => m.recorded || m.recording_url || m.daily_recording_id);
    const failed = happened.filter(
      (m) => !(m.recorded || m.recording_url || m.daily_recording_id) && (m.duration_minutes ?? 0) > 1,
    );
    const rate = happened.length > 0 ? (withRec.length / happened.length) * 100 : 100;
    return {
      total: happened.length,
      withRecording: withRec.length,
      failed,
      rate,
    };
  }, [meetings]);

  const byDay = useMemo(() => {
    const map = new Map<string, { date: string; total: number; withRec: number; failed: number }>();
    for (const m of meetings || []) {
      if (!m.started_at && !m.duration_minutes) continue;
      const key = dayKey(m.started_at || m.scheduled_for);
      const cur = map.get(key) || { date: key, total: 0, withRec: 0, failed: 0 };
      cur.total += 1;
      if (m.recorded || m.recording_url || m.daily_recording_id) cur.withRec += 1;
      else if ((m.duration_minutes ?? 0) > 1) cur.failed += 1;
      map.set(key, cur);
    }
    return Array.from(map.values());
  }, [meetings]);

  async function recoverOne(meetingId: string) {
    setRecovering(meetingId);
    try {
      const { data, error } = await supabase.functions.invoke("recover-orphan-meetings", {
        body: { meeting_id: meetingId },
      });
      if (error) throw error;
      const action = data?.results?.[0]?.action;
      if (action === "recovered") {
        toast.success("Gravação recuperada do Daily.co!");
      } else if (action === "no_recording_found") {
        toast.warning("Sem arquivo de gravação no Daily — sessão preenchida e tarefa de follow-up criada.");
      } else if (action === "already_processed") {
        toast.info("Reunião já processada.");
      } else {
        toast.error(`Falha: ${data?.results?.[0]?.error || "desconhecida"}`);
      }
      refetch();
    } catch (e: any) {
      toast.error("Erro ao recuperar: " + (e?.message || e));
    } finally {
      setRecovering(null);
    }
  }

  const rateColor =
    stats.rate >= 90 ? "text-emerald-600" : stats.rate >= 70 ? "text-amber-600" : "text-destructive";

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Video className="h-6 w-6 text-primary" />
            Saúde das Gravações de Reuniões
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Monitoramento das videochamadas Daily.co e taxa de captura de áudio.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={(v: Period) => setPeriod(v)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Últimos 7 dias</SelectItem>
              <SelectItem value="14d">Últimos 14 dias</SelectItem>
              <SelectItem value="30d">Últimos 30 dias</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCcw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Reuniões realizadas</p>
                <p className="text-3xl font-semibold mt-1">{stats.total}</p>
              </div>
              <Activity className="h-8 w-8 text-muted-foreground/40" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Com gravação</p>
                <p className="text-3xl font-semibold mt-1 text-emerald-600">{stats.withRecording}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-emerald-600/40" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Taxa de captura</p>
                <p className={`text-3xl font-semibold mt-1 ${rateColor}`}>{stats.rate.toFixed(1)}%</p>
              </div>
              {stats.rate >= 90 ? (
                <CheckCircle2 className="h-8 w-8 text-emerald-600/40" />
              ) : (
                <AlertTriangle className="h-8 w-8 text-amber-500/40" />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Daily breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Por dia</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dia</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Com gravação</TableHead>
                <TableHead className="text-right">Falhas</TableHead>
                <TableHead className="text-right">Taxa</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {byDay.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                    Sem reuniões no período.
                  </TableCell>
                </TableRow>
              )}
              {byDay.map((d) => {
                const rate = d.total > 0 ? (d.withRec / d.total) * 100 : 0;
                const color =
                  rate >= 90 ? "text-emerald-600" : rate >= 70 ? "text-amber-600" : "text-destructive";
                return (
                  <TableRow key={d.date}>
                    <TableCell className="font-medium">{d.date}</TableCell>
                    <TableCell className="text-right">{d.total}</TableCell>
                    <TableCell className="text-right text-emerald-600">{d.withRec}</TableCell>
                    <TableCell className="text-right text-destructive">{d.failed}</TableCell>
                    <TableCell className={`text-right font-semibold ${color}`}>
                      {rate.toFixed(0)}%
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Failed meetings list */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Reuniões sem gravação ({stats.failed.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Sala</TableHead>
                <TableHead className="text-right">Duração</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats.failed.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                    🎉 Nenhuma reunião sem gravação no período.
                  </TableCell>
                </TableRow>
              )}
              {stats.failed.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="text-sm">
                    {m.started_at
                      ? new Date(m.started_at).toLocaleString("pt-BR", {
                          day: "2-digit",
                          month: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—"}
                  </TableCell>
                  <TableCell className="text-xs font-mono text-muted-foreground">
                    {m.daily_room_name?.slice(0, 12) || "—"}
                  </TableCell>
                  <TableCell className="text-right">{m.duration_minutes ?? 0} min</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {m.status || "—"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => recoverOne(m.id)}
                        disabled={recovering === m.id}
                      >
                        {recovering === m.id ? "..." : "Recuperar"}
                      </Button>
                      {m.deal_id && (
                        <Button size="sm" variant="ghost" asChild>
                          <Link to={`/dealdetail?id=${m.deal_id}`}>
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Link>
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Saúde de transferências de chamadas */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <PhoneForwarded className="h-4 w-4 text-primary" />
            Transferências de chamadas (período)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-2xl font-semibold">{transferStats.total}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Com gravação completa</p>
              <p className="text-2xl font-semibold text-emerald-600">{transferStats.withFullRec}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Taxa de captura</p>
              <p
                className={`text-2xl font-semibold ${
                  transferStats.rate >= 90
                    ? "text-emerald-600"
                    : transferStats.rate >= 70
                    ? "text-amber-600"
                    : "text-destructive"
                }`}
              >
                {transferStats.rate.toFixed(1)}%
              </p>
            </div>
          </div>
          {transferStats.failed.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Duração</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transferStats.failed.slice(0, 25).map((t: any) => (
                  <TableRow key={t.id}>
                    <TableCell className="text-sm">
                      {t.transferred_at
                        ? new Date(t.transferred_at).toLocaleString("pt-BR", {
                            day: "2-digit",
                            month: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {t.transfer_type || "—"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{t.duration_seconds ?? 0}s</TableCell>
                    <TableCell className="text-right">
                      {t.deal_id && (
                        <Button size="sm" variant="ghost" asChild>
                          <Link to={`/dealdetail?id=${t.deal_id}`}>
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Link>
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {transferStats.total === 0 && (
            <p className="text-center text-muted-foreground py-4 text-sm">
              Sem transferências no período.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
